import { graphql } from "gatsby";
import { useIntl } from "../hooks/useIntl";
import React, { useCallback } from "react";
import { formatDate } from "../helpers/date";

import Page from "../components/Page";
import { PressReleaseSearch } from "../components/ArticlesSearch";
import { getTranslations } from "../helpers/translations";
import { Tag, TagType } from "../types.d.ts";
import { getAssetURL } from "../helpers/assets";
import { PressReleaseCardProps } from "../components/PressReleaseCard";
import SEO from "../components/SEO";
import { parseSEO } from "../helpers/seo";
import { isArticleValid } from "../helpers/article";
import { useStaticLabels } from "../hooks/useStaticLabels";
import { isPublished } from "../helpers/directus";

const PressRelease = ({ data }: { data: any }) => {
  const intl = useIntl();
  const labels = useStaticLabels();

  const getRootTranslatedData = useCallback(() => {
    return data?.directus?.article?.map((article: any) => {
      const translatedArticle =
        getTranslations(article, "translations", intl.locale) ?? {};
      return {
        ...translatedArticle,
        article_slug: article?.article_slug,
        article_type: article?.article_type,
      };
    });
  }, [data]);

  const getPressReleases = useCallback((): PressReleaseCardProps[] => {
    const articles = getRootTranslatedData();

    return articles
      .filter(
        (article: any) =>
          article?.article_type?.type_value === TagType?.PRESS_RELEASE
      )
      .map((article: any) => {
        const translatedArticleType = getTranslations(
          article,
          "article_type.translations",
          intl.locale
        );

        const parsedTags: Tag[] = article?.tags?.map((tag: any) => {
          const translatedTag = getTranslations(
            tag,
            "article_tag_id.translations",
            intl.locale
          );
          return {
            label: translatedTag?.tag_name,
            value: translatedTag?.tag_name, //tag?.article_tag_id?.name,
          };
        });
        return {
          status: article?.status,
          infoStatus: article?.info_status,
          title: article?.article_title,
          image: {
            src: getAssetURL(article?.article_image),
            alt: getAssetURL(article?.article_image_alt),
          },
          thumbnail: {
            src: getAssetURL(article?.article_thumbnail),
            alt: getAssetURL(article?.article_thumbnail_alt),
          },
          slug: article?.article_slug,
          date: formatDate(article?.article_date),
          readMore: labels?.readMoreLabel,
          type: translatedArticleType?.article_type_label,
          tags: parsedTags,
          description: article?.article_description,
        };
      })
      ?.filter(
        (a: any) =>
          isArticleValid(a) &&
          isPublished(a?.status) &&
          isPublished(a?.infoStatus)
      )
      ?.sort(
        (a, b) => new Date(b?.date).getTime() - new Date(a?.date).getTime()
      );
  }, [getRootTranslatedData, intl]);

  const getTags = useCallback((): Tag[] => {
    const tags = data?.directus?.article_tag;

    return [
      ...tags
        .filter(
          (tag: any) => tag?.tag_type?.type_value === TagType.PRESS_RELEASE
        )
        .map((tag: any) => {
          const translatedTag = getTranslations(
            tag,
            "translations",
            intl.locale
          );
          return {
            label: translatedTag?.tag_name,
            value: translatedTag?.tag_name,
          };
        }),
    ];
  }, [data, intl]);

  return (
    <Page
      initialHeaderBackgroundColor={"var(--color-green-90)"}
      helmet={<SEO {...parseSEO(getRootTranslatedData(), intl?.locale)} />}
    >
      <PressReleaseSearch
        title={labels?.allPressReleasesTitle}
        backLabel={labels?.backToNewsroomLabel}
        searchLabel={labels?.allPressSearchPlaceholder}
        tags={getTags()}
        pressReleases={getPressReleases()}
      />
    </Page>
  );
};

export default PressRelease;

export const query = graphql`
  query ($locale: String) {
    directus {
      article(limit: -1) {
        article_slug
        article_type {
          type_value
          translations(filter: { languages_code: { code: { _eq: $locale } } }) {
            languages_code {
              name
              code
            }
            article_type_label
          }
        }
        id
        translations(filter: { languages_code: { code: { _eq: $locale } } }) {
          seo_information {
            ...SEO
          }
          languages_code {
            name
            code
          }
          status
          tags {
            article_tag_id {
              tag_type {
                type_value
                translations(
                  filter: { languages_code: { code: { _eq: $locale } } }
                ) {
                  languages_code {
                    name
                    code
                  }
                  article_type_label
                }
              }
              translations(
                filter: { languages_code: { code: { _eq: $locale } } }
              ) {
                languages_code {
                  name
                  code
                }
                tag_name
              }
            }
          }
          info_status
          article_title
          article_description
          article_date
          article_image_alt
          article_image {
            id
            filename_disk
          }
          article_thumbnail_alt
          article_thumbnail {
            id
            filename_disk
          }
          article_file {
            id
            filename_disk
          }
          article_content
          article_questions_title
          article_questions_accordion
        }
      }
      article_tag {
        tag_type {
          type_value
          status
          id
          translations(filter: { languages_code: { code: { _eq: $locale } } }) {
            id
            languages_code {
              code
              name
            }
            article_type_label
          }
        }
        id
        translations(filter: { languages_code: { code: { _eq: $locale } } }) {
          id
          languages_code {
            code
            name
          }
          tag_name
        }
      }
    }
  }
`;
